import { CommonModule, LowerCasePipe } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, Output, SimpleChanges, ViewChild } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { TreatmentTypeFile } from 'app/core/models';
import { DragDropDirective } from 'app/core/directive';
import { FormFile } from 'app/core/models/form-file';
import { TreatmentTypePreviewImages } from 'app/core/constants';
import { BytesPipe, TranslateValuePipe } from 'app/shared/pipes';
import { ProgressBar } from 'primeng/progressbar';
import { FeatureFlagStoreService } from 'app/core/services/feature-flag-store.service';
import { ToastModule } from 'primeng/toast';
import { SafePipe } from 'safe-pipe';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

const defaultAcceptMask = '*.*';

// Asks a mobile picker not to filter. Both tokens are needed, one per engine: WebKit returns an
// empty type set the moment it sees '*/*', which lands the iOS picker on public.item; Chromium
// instead skips its MIME restriction only when a generic type is present, and reaching that
// branch also stops Android offering the camera and gallery for what is a 3D scan.
const unfilteredAccept = 'application/octet-stream,*/*';

// Mobile pickers match `accept` against the platform type registry, not against file names,
// and neither registry resolves these: iOS has no UTType, so its picker greys every file out,
// and Android maps .stl to application/vnd.ms-pki.stl, which no provider reports.
const unresolvableExtensions = new Set(['ply', 'obj', 'stl']);

// iPhone cameras write HEIC, so image uploads have to expect it; changeTrigger converts to JPEG.
const heicExtensionPattern = /\.hei[cf]$/i;

function isHeicFile(file: File): boolean {
  return file.type === 'image/heic' || file.type === 'image/heif' || heicExtensionPattern.test(file.name);
}

@Component({
  selector: 'wl-upload-scans',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    TranslateValuePipe,
    ProgressBar,
    ToastModule,
    DragDropDirective,
    SafePipe,
    LowerCasePipe,
  ],
  templateUrl: './upload-scans.component.html',
  styleUrl: './upload-scans.component.scss',
})
export class UploadScansComponent {
  @Input() file!: TreatmentTypeFile;
  @Input() value!: any;
  @Input() minBytes!: number;
  @Input() isDroppable: boolean = true;
  @Input() allowUploadOnlyFolder = false;
  @Input() checkMaxFilesCount = true;
  @Input() isUploadYourDesign: boolean = false;
  @Output() fileSelected = new EventEmitter();
  @Output() fileRemoved = new EventEmitter<FormFile>();
  // @Input() uploading: boolean = false;

  @Output() cloudDriveEvent = new EventEmitter<void>();

  @ViewChild('fileInput') fileInput!: ElementRef;

  showCloudDrive$!: Observable<boolean>;
  imagePath!: string;
  maxMegabytes!: number;
  required = false;
  multiple: boolean = false;
  accept: string = defaultAcceptMask;

  maxFilesCount = 10;
  bytesInMegabyte = 1048576;
  maxSizeExceeded = false;
  displayAcceptError: boolean = false;
  displaySingleFileOnlyError: boolean = false;
  displayMaxFilesCountError: boolean = false;
  displayMinBitesError: boolean = false;
  displayConversionError: boolean = false;

  constructor(private featureFlagService: FeatureFlagStoreService) {}

  ngOnInit(): void {
    this.showCloudDrive$ = this.featureFlagService.featureFlags$.pipe(map(flags => flags['cloud-drive'] as boolean));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['file'] && this.file) {
      const { imagePath, accept, required, multiple, maxMegabytes } = this.file;
      // TreatmentTypeFile.Accept is a nullable column, and acceptAttribute reads this on every
      // change-detection pass, so a null would throw for the whole page rather than one tile.
      this.accept = accept || defaultAcceptMask;
      this.required = required;
      this.multiple = multiple;
      this.maxMegabytes = maxMegabytes;
      this.imagePath = imagePath;
    }
  }

  /**
   * Value handed to the native file picker. Touch devices cannot resolve scan extensions,
   * so their picker is asked not to filter; `accept` itself stays strict and remains what
   * changeTrigger validates against.
   */
  get acceptAttribute(): string {
    if (this.accept === defaultAcceptMask) {
      return '';
    }

    if (this.isTouchDevice && this.hasUnresolvableExtension) {
      return unfilteredAccept;
    }

    return this.acceptsJpeg ? `${this.accept},.heic,.heif` : this.accept;
  }

  private get hasUnresolvableExtension(): boolean {
    return this.parseAcceptExtensions().some(extension => unresolvableExtensions.has(extension));
  }

  private get acceptsJpeg(): boolean {
    const extensions = this.parseAcceptExtensions();

    return extensions.includes('jpg') || extensions.includes('jpeg');
  }

  get hasError(): boolean {
    return (
      this.displayAcceptError ||
      this.displaySingleFileOnlyError ||
      this.displayMaxFilesCountError ||
      this.displayMinBitesError ||
      this.displayConversionError ||
      this.maxSizeExceeded
    );
  }

  private get isTouchDevice(): boolean {
    return typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)')?.matches === true;
  }

  openFileDialog() {
    this.fileInput.nativeElement.click();
  }

  removeFile(file: FormFile) {
    const files = this.value;
    files.splice(this.value.indexOf(file), 1);
    this.value = Object.assign([], files);
    this.fileRemoved.emit(file);
  }

  async changeTrigger(event: any) {
    this.displaySingleFileOnlyError = false;
    this.displayMaxFilesCountError = false;
    this.displayMinBitesError = false;
    this.displayAcceptError = false;
    this.displayConversionError = false;
    this.maxSizeExceeded = false;
    let files: any[] = Array.from(event?.files ?? event);

    if (!files || files.length === 0) {
      return;
    } else if (!this.multiple && files.length > 1) {
      this.displaySingleFileOnlyError = true;
      return;
    } else if (
      this.checkMaxFilesCount &&
      !this.allowUploadOnlyFolder &&
      this.multiple &&
      files.length > this.maxFilesCount
    ) {
      this.displayMaxFilesCountError = true;

      files = files.slice(0, this.maxFilesCount);
    }

    // Only after the count caps: converting a photo the component is about to discard costs a
    // full-resolution decode for nothing. The size checks below then measure what is uploaded.
    files = await this.normalizeHeicFiles(files);

    if (files.length === 0) {
      return;
    }

    if (this.minBytes) {
      if (files.find(x => x.size <= this.minBytes)) {
        this.displayMinBitesError = true;
        return;
      }
    }

    const sizeSum = files.map(f => f.size).reduce((a, b) => a + b, 0);

    if (!this.maxSize || sizeSum <= this.maxSize) {
      this.maxSizeExceeded = false;

      let formFiles: any[] = files.map(f => {
        const normalizedFileType = this.getFileExtension(f.name);

        return {
          id: null,
          s3FileId: null,
          progress: 0,
          inProgress: true,
          name: f.name,
          type: normalizedFileType,
          size: f.size,
          notes: '',
          file: f,
          createdDate: new Date().toISOString(),
        };
      });

      if (this.accept !== defaultAcceptMask) {
        const normalizedAcceptTypes = this.parseAcceptExtensions();

        formFiles = formFiles.filter(f => {
          const normalizedFileType = this.getFileExtension(f.name);

          const isCorrectType = normalizedAcceptTypes.some(t => t === normalizedFileType);

          if (!isCorrectType) {
            this.displayAcceptError = true;
          }

          return isCorrectType;
        });
      }

      this.value = [...this.value, ...formFiles];

      this.fileSelected.emit(formFiles);
    } else {
      this.maxSizeExceeded = true;
    }

    if (this.fileInput?.nativeElement?.value) {
      this.fileInput.nativeElement.value = null;
    }
  }

  get maxSize(): number {
    return this.maxMegabytes ? this.maxMegabytes * this.bytesInMegabyte : 0;
  }

  /**
   * Replaces any HEIC the user picked with a JPEG before the extension checks run, so a photo
   * straight off an iPhone is accepted rather than rejected for its format.
   */
  private async normalizeHeicFiles(files: any[]): Promise<any[]> {
    if (!this.acceptsJpeg || !files.some(file => isHeicFile(file))) {
      return files;
    }

    const converted: any[] = [];

    // Sequential on purpose: each decode holds a full-resolution bitmap, and running a camera
    // roll's worth of them at once is what locks up the phone this change is meant to serve.
    for (const file of files) {
      if (!isHeicFile(file)) {
        converted.push(file);
        continue;
      }

      try {
        converted.push(await this.convertHeicToJpeg(file));
      } catch {
        this.displayConversionError = true;
      }
    }

    return converted;
  }

  private async convertHeicToJpeg(file: File): Promise<File> {
    // Loaded on demand: the converter is about 1.3 MB and most uploads never need it.
    const { default: heic2any } = await import('heic2any');
    const result = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.92 });
    const blob = Array.isArray(result) ? result[0] : result;

    return new File([blob], file.name.replace(heicExtensionPattern, '.jpg'), { type: 'image/jpeg' });
  }

  private parseAcceptExtensions(): string[] {
    return this.accept
      .split(',')
      .map(token => token.trim().toLowerCase())
      .filter(token => token.startsWith('.') && token.length > 1)
      .map(token => token.slice(1));
  }

  getFileExtension(fileName: string, withDot: boolean = false) {
    const nameParts = fileName.split('.');
    return (withDot ? '.' : '') + nameParts[nameParts.length - 1].toLowerCase();
  }

  fileImagePath(file: TreatmentTypeFile) {
    const previewImage = TreatmentTypePreviewImages.find(
      x => x.name === file.name && x.treatmentTypeId === file.treatmentTypeId
    );
    return previewImage?.imagePath ?? '';
  }

  onFileDrop(fileList: FileList) {
    // We pass the FileList directly to the existing logic
    this.changeTrigger(fileList as any);
  }
}
